import React, { useEffect, useRef, useState } from "react";
import BackButton from "@/components/BackButton";
import { MicRow, appendText } from "@/components/VoiceMic";
import { useParams, Link } from "react-router-dom";
import { fetchProduct, resolveImageUrl } from "@/lib/api";
import { likeProduct, unlikeProduct, listComments, addComment, vendorToggleProductStock } from "@/lib/vendorApi";
import { useApp } from "@/context/AppContext";
import { ProductCard, priceOff, SpecialNoteBadge } from "@/components/Cards";
import AuthDialog from "@/components/AuthDialog";
import ShopStatusPill from "@/components/ShopStatusPill";
import { Heart, MapPin, Star, Truck, ShieldCheck, ArrowLeft, MessageCircle, ThumbsUp, ChevronLeft, ChevronRight, Loader2, PackageCheck, PackageX } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function ProductDetail() {
  const { id } = useParams();
  const { pincode, toggleWishlist, inWishlist, user, formatApiError } = useApp();
  const [product, setProduct] = useState(null);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeBusy, setLikeBusy] = useState(false);

  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [stockBusy, setStockBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchProduct(id, pincode)
      .then((p) => {
        setProduct(p);
        setLiked(!!p.liked_by_me);
        setLikeCount(p.like_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    listComments(id).then(setComments).catch(() => {});
  }, [id, pincode, user?.id]);

  const toggleLike = async () => {
    if (!user) { toast.error("Please login to like."); setAuthOpen(true); return; }
    setLikeBusy(true);
    // Optimistic
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount((c) => c + (wasLiked ? -1 : 1));
    try {
      if (wasLiked) await unlikeProduct(id);
      else await likeProduct(id);
    } catch (e) {
      // Rollback
      setLiked(wasLiked);
      setLikeCount((c) => c + (wasLiked ? 1 : -1));
      toast.error(formatApiError(e));
    } finally {
      setLikeBusy(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please login to comment."); setAuthOpen(true); return; }
    const text = comment.trim();
    if (!text) { toast.error("Comment cannot be empty"); return; }
    setCommentBusy(true);
    try {
      const created = await addComment(id, text);
      setComments((c) => [created, ...c]);
      setComment("");
      toast.success("Comment posted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCommentBusy(false);
    }
  };

  // Vendor-only availability toggle. Backend enforces shop ownership on PATCH /vendor/products/:id/stock
  const toggleAvailability = async () => {
    if (!product || !product.is_owner) return;
    const current = product.in_stock !== false;
    const next = !current;
    setStockBusy(true);
    // Optimistic update
    setProduct((p) => (p ? { ...p, in_stock: next } : p));
    try {
      const res = await vendorToggleProductStock(id, next);
      // Re-sync from server response in case of normalization
      setProduct((p) => (p ? { ...p, in_stock: !!res.in_stock } : p));
      toast.success(next ? "Marked available" : "Marked unavailable");
    } catch (err) {
      // Rollback
      setProduct((p) => (p ? { ...p, in_stock: current } : p));
      toast.error(formatApiError(err));
    } finally {
      setStockBusy(false);
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-16 text-center text-slate-500">Loading...</div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-16 text-center">Product not found</div>;

  const off = priceOff(product.mrp, product.price);
  const discount = off?.pct || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10" data-testid="product-detail-page">
      <BackButton fallback="/category/products" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-6" testid="product-back-btn" />
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
        <div>
          <div className="relative aspect-square rounded-3xl overflow-hidden bg-slate-50" data-testid="product-gallery">
            <ProductGallery images={(product.images && product.images.length) ? product.images : [product.image]} name={product.name} onIndex={setGalleryIdx} />
            {(discount > 0 || product.special_note) && (
              <div className="absolute top-4 left-4 right-16 flex flex-col items-start gap-1.5 z-10 pointer-events-none">
                {discount > 0 && <div className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-testid="product-detail-discount-badge">{discount}% OFF</div>}
                <SpecialNoteBadge note={product.special_note} className="text-xs px-3 py-1" testid="product-detail-note-badge" />
              </div>
            )}
          </div>
          <ProductCaption product={product} idx={galleryIdx} />
        </div>
        <div>
          {product.same_pincode && (
            <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 text-xs font-semibold px-3 py-1 rounded-full mb-3">
              <MapPin className="w-3.5 h-3.5" /> Same pincode as you
            </div>
          )}
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-tight" data-testid="product-title">{product.name}</h1>
          <Link to={`/v/${product.shop_slug || product.shop_id}`} className="inline-flex items-center gap-2 mt-3 text-sm text-slate-600 hover:text-emerald-700 flex-wrap">
            <span>Sold by</span>
            <span className="font-semibold underline">{product.shop_name}</span>
            {product.shop_status && <ShopStatusPill status={product.shop_status} shopId={product.shop_id} />}
            <span>·</span>
            <MapPin className="w-3.5 h-3.5" /> {product.distance_km != null ? `${product.distance_km} km away` : "Distance unavailable"}
          </Link>

          <div className="flex items-center gap-3 mt-4">
            <div className="flex items-center gap-1 bg-emerald-50 text-emerald-800 px-2 py-1 rounded font-semibold text-sm">
              <Star className="w-3.5 h-3.5 fill-current" /> {product.rating}
            </div>
            <span className="text-sm text-slate-500">{product.reviews} reviews</span>
            <button onClick={toggleLike} disabled={likeBusy} className={`inline-flex items-center gap-1 text-sm font-semibold rounded-full px-3 py-1 border transition-colors ${liked ? "bg-red-50 text-red-600 border-red-100" : "bg-slate-50 text-slate-700 border-slate-200 hover:border-red-200"}`} data-testid="like-btn">
              <ThumbsUp className={`w-3.5 h-3.5 ${liked ? "fill-current" : ""}`} />
              <span data-testid="like-count">{likeCount}</span>
              <span className="hidden sm:inline">{liked ? "Liked" : "Like"}</span>
            </button>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-display text-4xl font-bold text-slate-900">₹{product.price}</div>
            {off && <div className="text-lg text-slate-400 line-through">₹{product.mrp}</div>}
            {off && <div className="text-sm font-semibold text-emerald-700" data-testid="product-detail-savings">{off.pct}% OFF · Save ₹{off.save}</div>}
          </div>
          <div className="text-sm text-slate-500 mt-1">{product.unit}</div>

          <p className="mt-6 text-slate-600 leading-relaxed">{product.description}</p>

          {/* Availability bar — vendor can toggle, customer sees status only */}
          <AvailabilityBar
            inStock={product.in_stock !== false}
            isOwner={!!product.is_owner}
            busy={stockBusy}
            onToggle={toggleAvailability}
          />


          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <button onClick={() => toggleWishlist(product)} className="w-12 h-12 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 flex-shrink-0" aria-label="Wishlist" data-testid="wishlist-detail">
              <Heart className={`w-5 h-5 ${inWishlist(product.id) ? "text-red-500 fill-red-500" : "text-slate-500"}`} />
            </button>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-2xl p-4">
              <Truck className="w-5 h-5 text-emerald-700 mb-2" />
              <div className="text-sm font-semibold">Fast local delivery</div>
              <div className="text-xs text-slate-500">Within {product.same_pincode ? "2 hours" : "6-8 hours"}</div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-4">
              <ShieldCheck className="w-5 h-5 text-emerald-700 mb-2" />
              <div className="text-sm font-semibold">Verified vendor</div>
              <div className="text-xs text-slate-500">Trusted local shop</div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments */}
      <div className="mt-16" data-testid="comments-section">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-5 h-5 text-emerald-700" />
          <h2 className="font-display text-2xl font-bold">Comments ({comments.length})</h2>
        </div>
        <form onSubmit={submitComment} className="flex gap-2 items-start mb-6">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-semibold text-sm flex-shrink-0">
            {user ? user.name?.[0]?.toUpperCase() : "?"}
          </div>
          <div className="flex-1">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder={user ? "Share your experience..." : "Please login to comment."}
              onClick={() => { if (!user) setAuthOpen(true); }}
              readOnly={!user}
              className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-emerald-500 resize-none"
              data-testid="comment-input"
            />
            {user && <MicRow onText={(t) => setComment((v) => appendText(v, t).slice(0, 500))} testid="mic-comment" />}
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-slate-400">{comment.length}/500</span>
              <button type="submit" disabled={commentBusy || !comment.trim()} className="bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white rounded-full px-4 py-1.5 text-xs font-semibold" data-testid="comment-submit">
                {commentBusy ? "Posting..." : user ? "Post comment" : "Login to comment"}
              </button>
            </div>
          </div>
        </form>

        <div className="space-y-4">
          {comments.length === 0 ? (
            <div className="text-sm text-slate-500">Be the first to comment on this product.</div>
          ) : (
            comments.map((c, i) => (
              <div key={`${c.user_id}-${c.created_at}`} className="flex gap-3" data-testid={`comment-${i}`}>
                <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {c.user_name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 bg-white border border-slate-100 rounded-2xl p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{c.user_name || "User"}</span>
                    <span className="text-xs text-slate-400">{timeAgo(c.created_at)}</span>
                  </div>
                  <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{c.comment}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {product.related && product.related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-display text-2xl font-bold mb-6">More from {product.shop_name}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
            {product.related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}

function AvailabilityBar({ inStock, isOwner, busy, onToggle }) {
  // Public (customer) read-only view: a slim status bar so shoppers immediately
  // know if the item can be ordered. Keeps the existing product-detail aesthetic.
  if (!isOwner) {
    return (
      <div className={`mt-5 flex items-center gap-2 rounded-xl border px-3 py-2.5 ${inStock ? "border-emerald-100 bg-emerald-50/60" : "border-rose-100 bg-rose-50/60"}`} data-testid="availability-bar-public">
        {inStock ? (
          <PackageCheck className="w-4 h-4 text-emerald-700 flex-shrink-0" />
        ) : (
          <PackageX className="w-4 h-4 text-rose-700 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${inStock ? "text-emerald-800" : "text-rose-800"}`} data-testid="availability-status-text">
            {inStock ? "Available for order" : "Currently unavailable"}
          </div>
          <div className="text-[11px] text-slate-500 truncate">{inStock ? "Vendor is accepting orders right now." : "The vendor has paused this product. Please check back soon."}</div>
        </div>
      </div>
    );
  }

  // Vendor-owner view: a visible, tappable slider that persists to the server.
  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-white px-3 py-3" data-testid="availability-bar-owner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Product availability</div>
          <div className={`text-sm font-semibold ${inStock ? "text-emerald-700" : "text-slate-700"} truncate`}>{inStock ? "Available — customers can order" : "Unavailable — hidden from cart"}</div>
        </div>
        <button
          onClick={onToggle}
          disabled={busy}
          aria-label="Toggle product availability"
          aria-pressed={inStock}
          data-testid="availability-slider"
          className={`relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-60 ${inStock ? "bg-emerald-600" : "bg-slate-300"}`}
        >
          <span className={`pointer-events-none inline-flex h-6 w-6 items-center justify-center transform rounded-full bg-white shadow ring-0 transition ${inStock ? "translate-x-7" : "translate-x-0"}`}>
            {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
          </span>
        </button>
      </div>
    </div>
  );
}

function ProductCaption({ product, idx }) {
  const imgs = ((product.images && product.images.length) ? product.images : [product.image]).filter(Boolean);
  const c = (product.image_captions || {})[imgs[idx]];
  if (!c) return null;
  return <div className="mt-2 px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-100 text-xs sm:text-sm text-slate-600 text-center break-words" data-testid="product-gallery-caption">{c}</div>;
}

function ProductGallery({ images, name, onIndex }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { onIndex?.(idx); }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setIdx(0); }, [images.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  const trackRef = useRef(null);
  const imgs = (images || []).filter(Boolean);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (imgs.length < 2 || paused) return;
    const t = setInterval(() => {
      const el = trackRef.current;
      if (!el || document.hidden) return;
      const next = (Math.round(el.scrollLeft / el.clientWidth) + 1) % imgs.length;
      el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
    }, 4000);
    return () => clearInterval(t);
  }, [imgs.length, paused]);
  if (imgs.length === 0) return <div className="w-full h-full bg-slate-100" />;

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const next = Math.round(el.scrollLeft / el.clientWidth);
    if (next !== idx) setIdx(next);
  };
  const go = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(imgs.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
    setIdx(clamped);
  };

  return (
    <>
      <div
        ref={trackRef}
        onScroll={onScroll}
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)} onTouchEnd={() => setTimeout(() => setPaused(false), 6000)}
        className="w-full h-full flex overflow-x-auto snap-x snap-mandatory hide-scrollbar"
        data-testid="gallery-track"
      >
        {imgs.map((src, i) => (
          <div key={i} className="min-w-full snap-center h-full">
            <img src={resolveImageUrl(src)} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" loading={i === 0 ? "eager" : "lazy"} data-testid={`gallery-image-${i}`} />
          </div>
        ))}
      </div>
      {imgs.length > 1 && (
        <>
          <button type="button" onClick={() => go(idx - 1)} className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 rounded-full p-2 shadow-md z-10" aria-label="Prev" data-testid="gallery-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => go(idx + 1)} className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-900 rounded-full p-2 shadow-md z-10" aria-label="Next" data-testid="gallery-next">
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10" data-testid="gallery-dots">
            {imgs.map((_, i) => (
              <button key={i} type="button" onClick={() => go(i)} className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-white w-6" : "bg-white/60"}`} aria-label={`Go to ${i + 1}`} data-testid={`gallery-dot-${i}`} />
            ))}
          </div>
        </>
      )}
    </>
  );
}
